import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  updatePublicEmailPreferences,
  verifyPublicEmailPreferences,
} from "api/bMarketingApi";

const shellStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  background: "linear-gradient(160deg, #f8fafc 0%, #e2e8f0 45%, #cbd5e1 100%)",
  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
};

const cardStyle = {
  width: "100%",
  maxWidth: "560px",
  background: "#ffffff",
  borderRadius: "24px",
  padding: "32px",
  boxShadow: "0 24px 80px rgba(15, 23, 42, 0.18)",
  border: "1px solid rgba(148, 163, 184, 0.25)",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 12px",
  borderRadius: "999px",
  background: "#e2e8f0",
  color: "#334155",
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const checkboxRowStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: "14px",
  padding: "18px 20px",
  borderRadius: "18px",
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  marginTop: "24px",
};

const primaryButtonStyle = {
  border: "none",
  borderRadius: "999px",
  padding: "14px 20px",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer",
};

function MarketingEmailPreferencesPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [contact, setContact] = useState(null);
  const [emailMarketing, setEmailMarketing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const userId = (searchParams.get("user_id") || "").trim();
  const token = (searchParams.get("token") || "").trim();

  useEffect(() => {
    let cancelled = false;

    const verifyLink = async () => {
      if (!userId || !token) {
        setStatus("invalid");
        setError("This link is missing the information required to load your preferences.");
        return;
      }

      setStatus("loading");
      setError("");

      try {
        const response = await verifyPublicEmailPreferences(userId, token);
        if (cancelled) {
          return;
        }

        setContact(response.data.contact || null);
        setEmailMarketing(Boolean(response.data.contact?.email_marketing));
        setStatus("ready");
      } catch (requestError) {
        if (cancelled) {
          return;
        }

        setStatus("invalid");
        setError(requestError.response?.data?.error || "This link is invalid or has expired.");
      }
    };

    verifyLink();

    return () => {
      cancelled = true;
    };
  }, [token, userId]);

  const handleSave = async (nextValue = emailMarketing) => {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await updatePublicEmailPreferences({
        user_id: userId,
        token,
        email_marketing: nextValue,
      });

      setContact(response.data.contact || null);
      setEmailMarketing(Boolean(response.data.contact?.email_marketing));
      setMessage(response.data.message || "Your preferences have been updated.");
    } catch (requestError) {
      setError(requestError.response?.data?.error || "We could not update your preferences right now.");
    } finally {
      setSaving(false);
    }
  };

  const title = status === "invalid" ? "An issue occured" : "Update your preferences";

  return (
    <div style={shellStyle}>
      <div style={cardStyle}>
        <div style={badgeStyle}>email preferences</div>
        <h1 style={{ margin: "18px 0 10px", fontSize: "34px", lineHeight: 1.05, color: "#0f172a" }}>{title}</h1>
        {status === "loading" ? (
          <p style={{ margin: 0, fontSize: "16px", lineHeight: 1.6, color: "#475569" }}>
            Verifying your link now.
          </p>
        ) : null}

        {status === "invalid" ? (
          <div>
            <p style={{ margin: "0 0 12px", fontSize: "16px", lineHeight: 1.6, color: "#475569" }}>
              {error || "This preferences link could not be verified."}
            </p>
            <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.6, color: "#64748b" }}>
              Please contact support@b-connect.co.uk if you need assistance.
            </p>
          </div>
        ) : null}

        {status === "ready" ? (
          <div>
            <p style={{ margin: "0 0 8px", fontSize: "16px", lineHeight: 1.6, color: "#475569" }}>
              Use this page to manage the email marketing status for <strong style={{ color: "#0f172a" }}>{contact?.email_address}</strong>.
            </p>

            <label style={checkboxRowStyle}>
              <input
                type="checkbox"
                checked={emailMarketing}
                onChange={(event) => {
                  setEmailMarketing(event.target.checked);
                  setMessage("");
                }}
                style={{ marginTop: 4, width: 18, height: 18 }}
              />
              <span>
                <span style={{ display: "block", fontSize: "16px", fontWeight: 600, color: "#0f172a" }}>
                  Receive marketing emails
                </span>
                <span style={{ display: "block", marginTop: 4, fontSize: "14px", lineHeight: 1.6, color: "#64748b" }}>
                  Keep the box checked to stay subscribed, or uncheck it if you no longer want email marketing from this organisation.
                </span>
              </span>
            </label>

            {message ? (
              <p style={{ margin: "18px 0 0", fontSize: "14px", lineHeight: 1.6, color: "#166534" }}>{message}</p>
            ) : null}
            {error ? (
              <p style={{ margin: "18px 0 0", fontSize: "14px", lineHeight: 1.6, color: "#b91c1c" }}>{error}</p>
            ) : null}

            <div style={{ display: "flex", justifyContent: "center", marginTop: "28px" }}>
              <button
                type="button"
                onClick={() => handleSave(emailMarketing)}
                disabled={saving}
                style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1 }}
              >
                {saving ? "Saving..." : "Update preferences"}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default MarketingEmailPreferencesPage;